import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { api, Site } from '../lib/api';

interface SiteState {
  site: Site | null;
  loading: boolean;
  refresh: () => void;
}

const SiteContext = createContext<SiteState>({ site: null, loading: true, refresh: () => {} });

export function SiteProvider({ children }: { children: ReactNode }) {
  const [site, setSite] = useState<Site | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchSite = () => {
    api.site
      .info()
      .then((res) => setSite(res.data))
      .catch(() => setSite(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchSite(); }, []);

  // Dynamically update page title and meta tags from backend site info
  useEffect(() => {
    if (!site) return;
    const meta = site.meta_tags || {};
    document.title = meta.og_title || site.name || 'Forum';
    const setMeta = (attr: string, key: string, value: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el && value) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      if (el) el.content = value;
    };
    const desc = meta.description || site.description || '';
    setMeta('name', 'description', desc);
    if (meta.keywords) setMeta('name', 'keywords', meta.keywords);
    setMeta('property', 'og:site_name', site.name || '');
    setMeta('property', 'og:title', meta.og_title || site.name || '');
    setMeta('property', 'og:description', desc);
    if (meta.og_image) setMeta('property', 'og:image', meta.og_image);
    setMeta('name', 'twitter:card', meta.twitter_card || 'summary');
    setMeta('name', 'twitter:title', meta.og_title || site.name || '');
    setMeta('name', 'twitter:description', desc);
    if (meta.canonical_url) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = meta.canonical_url;
    }
  }, [site]);

  return <SiteContext.Provider value={{ site, loading, refresh: fetchSite }}>{children}</SiteContext.Provider>;
}

export function useSite() {
  return useContext(SiteContext);
}
